import { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { DataTable, NativeSelect, money } from "./financeUi";

export default function FinanceLedger({ accounts, parties }) {
  return (
    <Tabs defaultValue="account">
      <TabsList className="mb-5">
        <TabsTrigger data-testid="ledger-account-tab" value="account">Account Ledger</TabsTrigger>
        <TabsTrigger data-testid="ledger-party-tab" value="party">Party Ledger</TabsTrigger>
      </TabsList>
      <TabsContent value="account">
        <LedgerView kind="account" label="Account" options={accounts.map((a) => [a.account_id, `${a.code} · ${a.name}`])} />
      </TabsContent>
      <TabsContent value="party">
        <LedgerView kind="party" label="Party" options={parties.map((p) => [p.party_id, `${p.name} (${p.type})`])} />
      </TabsContent>
    </Tabs>
  );
}

function LedgerView({ kind, label, options }) {
  const [id, setId] = useState("");
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!id) return setData(null);
    api
      .get(`/admin/company-finance/ledger/${kind}/${id}`, { params: range })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load ledger"));
  }, [id, range, kind]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr]">
        <NativeSelect data-testid={`ledger-${kind}-select`} onChange={(e) => setId(e.target.value)} value={id}>
          <option value="">Select {label.toLowerCase()}…</option>
          {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
        </NativeSelect>
        <Input data-testid={`ledger-${kind}-from`} onChange={(e) => setRange({ ...range, date_from: e.target.value })} type="date" value={range.date_from} />
        <Input data-testid={`ledger-${kind}-to`} onChange={(e) => setRange({ ...range, date_to: e.target.value })} type="date" value={range.date_to} />
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid={`ledger-${kind}-summary`}>
            {[["Opening", data.opening], ["Total debit", data.total_debit], ["Total credit", data.total_credit], ["Closing", data.closing]].map(([k, v]) => (
              <div className="bg-card border rounded-xl p-4" key={k}>
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="font-display text-lg font-bold tabular-nums" data-testid={`ledger-${kind}-${k.toLowerCase().replace(" ", "-")}`}>{money(v)}</div>
              </div>
            ))}
          </div>
          <DataTable
            columns={[
              ["Date", (r) => r.date],
              ["Voucher", (r) => <span className="font-mono text-xs">{r.voucher_number}</span>],
              ["Type", (r) => r.voucher_type],
              ["Particulars", (r) => r.narration || r.party || "—"],
              ["Debit", (r) => (r.debit ? money(r.debit) : ""), "right"],
              ["Credit", (r) => (r.credit ? money(r.credit) : ""), "right"],
              ["Balance", (r) => money(r.balance), "right"],
            ]}
            empty="No transactions in this period."
            rowKey={(r) => r.voucher_id}
            rows={data.rows}
            testid={`ledger-${kind}-table`}
          />
        </>
      )}
    </div>
  );
}
